import { getLeagueFlowTexts } from "../leagueFlowTexts";
import LeagueOverview from "./LeagueOverview";
import { useCallback, useState } from "react";
import type { LeagueCreateInput, LeagueUpdateInput } from "@tnp/contracts";
import Button from "../../../../shared/components/Button";
import Toast from "../../../../shared/components/Toast";
import { useAuth } from "../../../auth/shared/hooks/useAuth";
import { createLeague, getLeague, updateLeague } from "../../shared/services/leagueService";
import { useLeagueResource } from "../../shared/hooks/useLeagueResource";
import { getLeagueTexts } from "../../shared/leagueTexts";
import LeagueFeedback from "../../shared/components/LeagueFeedback";
import LeagueSettingsForm from "./LeagueSettingsForm";
import LeagueLoadError from "../../shared/components/LeagueLoadError";
import { getLeagueRosterTexts } from "../../shared/leagueRosterTexts";

import LeagueParticipantsEditor from "./LeagueParticipantsEditor";
export default function LeagueEditor({ id, onSaved }: { id?: string; onSaved?: (league: Awaited<ReturnType<typeof getLeague>>) => void }) {
  const { token } = useAuth();
  const t = getLeagueTexts();
  const f = getLeagueFlowTexts();
  const [toast, setToast] = useState<{ message: string; variant: "success" | "error" } | null>(null);
  const load = useCallback(() => id ? getLeague(id, token) : Promise.resolve(null), [id, token]);
  const resource = useLeagueResource(load);
  const [section, setSection] = useState<"overview" | "settings" | "participants">(id ? "overview" : "settings");
  const [rosterVisited, setRosterVisited] = useState(false);
  const [saved, setSaved] = useState<Awaited<ReturnType<typeof getLeague>> | null>(null);
  const league = saved || resource.data || undefined;
  async function save(settings: LeagueCreateInput | LeagueUpdateInput) {
    try {
      const result = league ? await updateLeague(token, league.id, settings as LeagueUpdateInput) : await createLeague(token, settings as LeagueCreateInput);
      setSaved(result);
      onSaved?.(result);
      if (!league) { setRosterVisited(true); setSection("participants"); }
      else setSection("overview");
      setToast({ message: league ? t.saved : f.newLeague, variant: "success" });
    } catch (reason) {
      setToast({ message: reason instanceof Error ? reason.message : t.loadError, variant: "error" });
    }
  }
  if (resource.loading) return <LeagueFeedback>{t.loading}</LeagueFeedback>;
  if (resource.error) return <LeagueLoadError error={resource.error} retry={resource.retry} />;
  return <>
    {league && (
      <div className="league-actions" role="group" aria-label={league.name}>
        <Button variant={section === "overview" ? "primary" : "ghost"} aria-pressed={section === "overview"} onClick={() => setSection("overview")}>{f.overview}</Button>
        <Button variant={section === "settings" ? "primary" : "ghost"} aria-pressed={section === "settings"} onClick={() => setSection("settings")}>{t.settings}</Button>
        <Button variant={section === "participants" ? "primary" : "ghost"} aria-pressed={section === "participants"} onClick={() => { setRosterVisited(true); setSection("participants"); }}>{getLeagueRosterTexts().title}</Button>
      </div>
    )}
    {league && section === "overview" && <LeagueOverview league={league} onParticipants={() => { setRosterVisited(true); setSection("participants"); }} />}
    <div hidden={section !== "settings"}>
      <LeagueSettingsForm key={`${league?.id || "new"}-${league?.revision || 0}`} league={league} onSave={save} />
    </div>
    {rosterVisited && league && <div hidden={section !== "participants"}>
      <LeagueParticipantsEditor key={`${league.id}-${league.revision}`} leagueId={league.id} />
    </div>}
    {toast && <Toast {...toast} onClose={() => setToast(null)} />}
  </>;
}
